import { useUser } from "@clerk/clerk-react";
import {
	Building2,
	ChevronLeft,
	ChevronRight,
	LayoutDashboard,
	Mail,
	MessageSquare,
	Package,
	Pencil,
	Plus,
	Settings,
	Ship,
	Trash2,
	Truck,
} from "lucide-react";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { avatarInitials } from "../../common/avatarInitials";
import { SIDEBAR_GROUPS } from "../shipmentAdapter";
import { autoName, displayName } from "./helpers";
import useInlineEdit from "./useInlineEdit";

export function NavSidebar({ collapsed, onToggle }) {
	const { user } = useUser();
	const userInitials = avatarInitials(user);
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const items = [
		{ key: "dashboard", label: "Dashboard", Icon: LayoutDashboard },
		// Shipments owns both the grid list (/preview/shipments) and the
		// per-shipment workspace — the only wired destination in the preview.
		{
			key: "shipments",
			label: "Shipments",
			Icon: Package,
			to: "/preview/shipments",
		},
		{ key: "providers", label: "Providers", Icon: Building2 },
		{ key: "settings", label: "Settings", Icon: Settings },
	];
	const ICON_PROPS = { size: 18, strokeWidth: 2 };
	return (
		<aside className={`ws-nav ${collapsed ? "ws-nav--collapsed" : ""}`}>
			<div className="ws-nav-brand">
				{!collapsed && (
					<span className="ws-nav-brand-name">MARTEU</span>
				)}
				<button
					className="ws-nav-collapse-btn"
					onClick={onToggle}
					title={collapsed ? "Expand" : "Collapse"}
				>
					{collapsed ? (
						<ChevronRight size={16} />
					) : (
						<ChevronLeft size={16} />
					)}
				</button>
			</div>
			<nav className="ws-nav-list">
				{items.map(({ key, label, Icon, to }) => {
					const active =
						key === "shipments"
							? pathname.startsWith("/preview/shipments") ||
								pathname.startsWith("/preview/workspace")
							: false;
					return (
						<button
							key={key}
							className={`ws-nav-link ${active ? "is-active" : ""}`}
							title={collapsed ? label : undefined}
							onClick={() => to && navigate(to)}
						>
							<span className="ws-nav-link-icon">
								<Icon {...ICON_PROPS} />
							</span>
							<span className="ws-nav-link-label">{label}</span>
						</button>
					);
				})}
			</nav>
			<div className="ws-nav-preview-tag">Preview</div>
			<div className="ws-nav-footer">
				<div className="ws-nav-avatar">{userInitials}</div>
				{!collapsed && (
					<div className="ws-nav-user">
						<span className="ws-nav-user-name">
							aaron@marteu.com
						</span>
						<span className="ws-nav-user-co">Marteu</span>
					</div>
				)}
			</div>
		</aside>
	);
}

// Group chrome for the status-grouped list, keyed by adapter group. Order of
// render comes from SIDEBAR_GROUPS (action first, done last) — this map only
// carries the label + dot/text classes.
const GROUP_META = {
	action_needed: { label: "Action needed", className: "is-action" },
	responses_received: {
		label: "Responses received",
		className: "is-responses",
	},
	submitted: { label: "Submitted", className: "is-submitted" },
	accepted: { label: "Accepted", className: "is-accepted" },
};

// Row title: "origin → destination · equipment". A user rename always wins;
// rows with no route yet (fresh drafts) fall back to their draft title.
function rowTitle(shipment, allShipments) {
	if (shipment.userName) return shipment.userName;
	const { originLabel, destinationLabel, equipmentLabel } = shipment;
	const hasRoute =
		originLabel &&
		originLabel !== "—" &&
		destinationLabel &&
		destinationLabel !== "—";
	if (!hasRoute) return displayName(shipment, allShipments);
	return `${originLabel} → ${destinationLabel}${equipmentLabel ? ` · ${equipmentLabel}` : ""}`;
}

// MM/DD HH:mm (24h) — compact last-activity stamp for the row meta column.
function formatActivityStamp(value) {
	if (!value) return "";
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return "";
	const pad = (n) => String(n).padStart(2, "0");
	return `${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Stable umber/navy pick per user so the same initiator always gets the same
// avatar color ("variety per user", not per render).
function avatarVariant(name) {
	let h = 0;
	for (const c of name || "") h = (h * 31 + c.charCodeAt(0)) % 997;
	return h % 2 === 0 ? "is-umber" : "is-navy";
}

function ModeIcon({ modeKind }) {
	if (modeKind === "ocean")
		return (
			<Ship size={16} strokeWidth={2} className="ws-row-mode is-ocean" />
		);
	if (modeKind === "domestic")
		return (
			<Truck
				size={16}
				strokeWidth={2}
				className="ws-row-mode is-domestic"
			/>
		);
	return (
		<MessageSquare
			size={16}
			strokeWidth={2}
			className="ws-row-mode is-draft"
		/>
	);
}

function InitiatorBadge({ initiator }) {
	if (!initiator) return null;
	if (initiator.type === "email") {
		return (
			<>
				<span
					className="ws-row-avatar is-email"
					title={initiator.address || "Created by email"}
				>
					<Mail size={10} strokeWidth={2} />
				</span>
				{initiator.address && (
					<span
						className="ws-row-initiator-email"
						title={initiator.address}
					>
						{initiator.address}
					</span>
				)}
			</>
		);
	}
	return (
		<span
			className={`ws-row-avatar ${avatarVariant(initiator.name)}`}
			title={initiator.name}
		>
			{initiator.initials}
		</span>
	);
}

export function SidebarItem({
	shipment,
	allShipments,
	isActive,
	onSelect,
	onRename,
	onDelete,
}) {
	// Single shared edit state machine. Sidebar rename has no lock concern, so
	// attemptSave commits immediately. The onCommit handler treats empty draft
	// as a clear (revert to auto-name).
	const edit = useInlineEdit({
		value: shipment.userName || autoName(shipment),
		onCommit: (val) => {
			const trimmed = (val || "").trim();
			const auto = autoName(shipment);
			const effective =
				trimmed.length === 0 || trimmed === auto ? null : trimmed;
			onRename(shipment.id, effective);
		},
	});
	const {
		editing,
		draft,
		setDraft,
		inputRef,
		begin: rawBegin,
		cancel,
		attemptSave,
		handleKey,
	} = edit;
	const beginEdit = (e) => {
		e.stopPropagation();
		rawBegin();
	};
	const onKeyDown = (e) => {
		e.stopPropagation();
		handleKey(e);
	};

	const group = shipment.group || "submitted";
	const title = rowTitle(shipment, allShipments);

	return (
		<div
			className={`ws-list-item ws-group-row ${GROUP_META[group]?.className || ""} ${isActive ? "is-active" : ""}`}
			onClick={() => !editing && onSelect(shipment.id)}
			role="button"
			tabIndex={0}
		>
			<ModeIcon modeKind={shipment.modeKind} />
			<div className="ws-row-main">
				<div className="ws-list-row">
					{editing ? (
						<input
							ref={inputRef}
							className="ws-list-name-input"
							value={draft}
							onChange={(e) => setDraft(e.target.value)}
							onBlur={attemptSave}
							onKeyDown={onKeyDown}
							onClick={(e) => e.stopPropagation()}
						/>
					) : (
						<span className="ws-list-name" title={title}>
							{title}
						</span>
					)}
					{!editing && (
						<>
							<button
								className="ws-list-edit-btn"
								onClick={beginEdit}
								aria-label="Rename shipment"
								tabIndex={-1}
							>
								<Pencil size={12} strokeWidth={2} />
							</button>
							{onDelete && shipment.status !== "BOOKED" && (
								<button
									className="ws-list-edit-btn"
									onClick={(e) => {
										e.stopPropagation();
										onDelete(shipment.id);
									}}
									aria-label="Delete shipment"
									tabIndex={-1}
									style={{ color: "var(--text-tertiary)" }}
								>
									<Trash2 size={12} strokeWidth={2} />
								</button>
							)}
						</>
					)}
				</div>
				<div className="ws-row-reason">
					{shipment.statusReason || ""}
				</div>
			</div>
			<div className="ws-row-meta">
				<InitiatorBadge initiator={shipment.initiator} />
				<span className="ws-row-time">
					{formatActivityStamp(
						shipment.lastActivityAt || shipment.createdAt,
					)}
				</span>
			</div>
		</div>
	);
}

export default function Sidebar({
	shipments,
	activeId,
	onSelect,
	onNew,
	onRename,
	onDelete,
	filter,
	onFilter,
}) {
	const filters = ["All", "Active", "Decide", "Booked"];
	const filtered = useMemo(() => {
		if (filter === "All") return shipments;
		if (filter === "Active")
			return shipments.filter((s) => s.status !== "BOOKED");
		if (filter === "Decide")
			return shipments.filter(
				(s) => s.status === "DECIDE" || s.status === "NEEDS_REPLY",
			);
		if (filter === "Booked")
			return shipments.filter((s) => s.status === "BOOKED");
		return shipments;
	}, [shipments, filter]);

	// Fixed-priority status groups; empty groups are hidden. Within a group,
	// most recent activity first.
	const grouped = useMemo(() => {
		const byActivity = (a, b) =>
			new Date(b.lastActivityAt || b.createdAt || 0) -
			new Date(a.lastActivityAt || a.createdAt || 0);
		return SIDEBAR_GROUPS.map((group) => ({
			group,
			items: filtered
				.filter((s) => (s.group || "submitted") === group)
				.sort(byActivity),
		})).filter(({ items }) => items.length > 0);
	}, [filtered]);

	return (
		<aside className="ws-sidebar">
			<div className="ws-sidebar-head">
				<button className="ws-new-btn" onClick={onNew}>
					<Plus size={15} strokeWidth={2.2} />
					New shipment
				</button>
				<div className="ws-sidebar-filter">
					{filters.map((f) => (
						<button
							key={f}
							className={`ws-filter-chip ${filter === f ? "is-active" : ""}`}
							onClick={() => onFilter(f)}
						>
							{f}
						</button>
					))}
				</div>
			</div>
			<div className="ws-sidebar-list">
				{grouped.map(({ group, items }) => (
					<div key={group} className="ws-group">
						<div
							className={`ws-group-head ${GROUP_META[group].className}`}
						>
							<span className="ws-group-dot" />
							<span className="ws-group-label">
								{GROUP_META[group].label}
							</span>
							<span className="ws-group-count">
								{items.length}
							</span>
						</div>
						{items.map((s) => (
							<SidebarItem
								key={s.id}
								shipment={s}
								allShipments={shipments}
								isActive={s.id === activeId}
								onSelect={onSelect}
								onRename={onRename}
								onDelete={onDelete}
							/>
						))}
					</div>
				))}
			</div>
		</aside>
	);
}
